import type React from 'react';

import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import { usePageScrollPosition, usePromoteCtaDisplayed } from '#src/app/pages/petitionDetails/shared/hooks';
import type { PageScrollPosition } from '#src/app/pages/petitionDetails/shared/hooks';

import { useUnauthenticatedSignForm } from '../../hooks/signForm/useUnauthenticatedSignForm';
import { PetitionStatusHeaderContainer } from '../PetitionStatusHeader';
import { SignatureStatsVictoryContainer } from '../stats/signatureStats';

import { MobileComboButtonContainer } from './MobileComboButton';
import { MobileRespondButtonContainer } from './MobileRespondButton';
import { MobileScrollToEmbeddedFormButtonContainer } from './MobileScrollToEmbeddedFormButton';
import { MobileShareButtonContainer } from './MobileShareButton';
import { MobileSignButtonContainer } from './MobileSignButton';

export type ActionPanelVariant = 'v1' | 'v2';

type Props = {
	sticky: boolean;
	variant: ActionPanelVariant;
};

const calculateVisibility = (
	sticky: boolean,
	{ beforeSuggestedPetitions, afterDescription, afterMobileActionButton, insideEmbeddedSignForm }: PageScrollPosition,
): boolean => {
	// Non-sticky button is always visible
	if (!sticky) {
		return true;
	}

	/**
	 * sticky button is only visible if page has scrolled after the non-sticky button and before suggested petitions
	 * !afterDescription condition allows sticky button to show in petitions with short descriptions
	 * !insideEmbeddedSignForm condition hides sticky button when embedded sign form CTA is in view
	 */
	return !!afterMobileActionButton && (beforeSuggestedPetitions || !afterDescription) && !insideEmbeddedSignForm;
};

export const MobileActionButtonContainer: React.FC<Props> = ({ sticky, variant }) => {
	const usePageScrollPositionContext = usePageScrollPosition();
	const {
		data: {
			petition: { status },
		},
	} = usePetitionDetailsPageData();
	const {
		data: {
			decisionMaker,
			signed,
			experiments: { supporterGrowthEmbedSignPetitionFormExperimentVariation },
		},
	} = usePetitionDetailsPageUserData();
	const promoteCtaDisplayed = usePromoteCtaDisplayed();
	const {
		data: { enabled: unauthenticatedSignFormEnabled },
	} = useUnauthenticatedSignForm();

	const visible = calculateVisibility(sticky, usePageScrollPositionContext);

	if (decisionMaker) {
		return <MobileRespondButtonContainer visible={visible} sticky={sticky} />;
	}

	if (status === 'CLOSED') {
		return <MobileShareButtonContainer visible={visible} sticky={sticky} hideSocialProof />;
	}

	if (status === 'VICTORY') {
		return (
			<div className="mb-6 flex flex-col gap-5">
				{!sticky && (
					<>
						<PetitionStatusHeaderContainer />
						<SignatureStatsVictoryContainer textAlign="center" />
					</>
				)}
				<MobileShareButtonContainer visible={visible} sticky={sticky} hideSocialProof />
			</div>
		);
	}

	if (signed && !unauthenticatedSignFormEnabled) {
		return promoteCtaDisplayed ? (
			<MobileComboButtonContainer visible={visible} sticky={sticky} />
		) : (
			<MobileShareButtonContainer visible={visible} sticky={sticky} />
		);
	}

	if (supporterGrowthEmbedSignPetitionFormExperimentVariation === 'variant_1') {
		return <MobileScrollToEmbeddedFormButtonContainer visible={visible} sticky={sticky} />;
	}

	return <MobileSignButtonContainer visible={visible} sticky={sticky} variant={variant} />;
};
